// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CART_BREAKPOINT } from "@eisinc/XDL/constants";
import BadgeAvailableToShip from "@eisinc/XDL/Icons/BadgeAvailableToShip";
import BadgeSchedulingUnavailable from "@eisinc/XDL/Icons/BadgeSchedulingUnavailable";
import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import { color, xScreen } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useMemo } from "react";
import { css } from "styled-components";

interface ScheduleOrderGroupHeaderProps {
    /** Whether this group is available to schedule */
    isAvailableToSchedule?: boolean;
    /** Custom header text override */
    headerText?: string;
}

interface ScheduleOrderGroupHeaderStyles {
    wrapper?: InjectableCss;
    descriptionStyles?: InjectableCss;
    columnHeadingsWrapper?: InjectableCss;
    columnTitle?: TypographyPresentationProps;
    columnTitleCentered?: TypographyPresentationProps;
    checkboxColumn?: InjectableCss;
}

const scheduleOrderGroupHeaderStyles: ScheduleOrderGroupHeaderStyles = {
    columnTitle: {
        weight: fontConfig.weight.medium,
        ...fontConfig.singleLineSmall,
        css: css`
            display: flex;
            align-items: center;
            ${IconWrapper} {
                margin-right: 8px;
            }
        `,
    },
    columnTitleCentered: {
        weight: fontConfig.weight.medium,
        ...fontConfig.singleLineSmall,
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
        `,
    },
    columnHeadingsWrapper: {
        css: css`
            display: contents;
            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
    wrapper: {
        css: css`
            display: grid;
            /* Checkbox | Image | Description | Total | QTY In Cart | Schedule QTY | Schedule Date */
            grid-template-columns: 30px 92px minmax(280px, 504px) 190px 124px 124px minmax(218px, 1fr);
            padding: 8px 0 8px 8px;
            gap: 16px;
            background: ${color("common.ui2")};

            ${({ borderColor }: any) => {
                if (borderColor) {
                    return css`
                        border-top: 4px solid ${getColor(borderColor)};
                    `;
                }
                return css`
                    padding-top: 8px;
                    box-shadow: 0 -2px 0 0 ${color("common.ui5")};
                `;
            }}

            ${({ showCheckboxColumn }: any) =>
                !showCheckboxColumn &&
                css`
                    grid-template-columns: 92px minmax(280px, 504px) 190px 124px 124px minmax(218px, 1fr);
                `}

            ${xScreen("lte", CART_BREAKPOINT)} {
                grid-template-columns: 30px 92px minmax(180px, 380px) 150px 100px 100px minmax(218px, 1fr);

                ${({ showCheckboxColumn }: any) =>
                    !showCheckboxColumn &&
                    css`
                        grid-template-columns: 92px minmax(200px, 380px) 150px 100px 100px minmax(120px, 1fr);
                    `}
            }

            ${xScreen("lte", "md")} {
                grid-template-columns: 1fr;
            }
        `,
    },
    descriptionStyles: {
        css: css`
            position: relative;
            grid-column: 1 / 4;

            ${({ showCheckboxColumn }: any) =>
                !showCheckboxColumn &&
                css`
                    grid-column: 1 / 3;
                `}
        `,
    },
    checkboxColumn: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
};

const statusConfig = {
    availableToSchedule: {
        color: "success",
        icon: BadgeAvailableToShip,
        defaultText: "Available to Schedule",
    },
    schedulingUnavailable: {
        color: "danger",
        icon: BadgeSchedulingUnavailable,
        defaultText: "Scheduling Unavailable",
    },
};

const ScheduleOrderGroupHeader: FC<ScheduleOrderGroupHeaderProps> = ({ isAvailableToSchedule = true, headerText }) => {
    const styles = useMemo(() => scheduleOrderGroupHeaderStyles, []);

    const status = isAvailableToSchedule ? statusConfig.availableToSchedule : statusConfig.schedulingUnavailable;
    const displayText = headerText || translate(status.defaultText);

    return (
        <StyledWrapper
            {...styles.wrapper}
            {...{ borderColor: status.color, showCheckboxColumn: isAvailableToSchedule }}
        >
            <StyledWrapper {...styles.descriptionStyles} {...{ showCheckboxColumn: isAvailableToSchedule }}>
                <Typography {...styles.columnTitle}>
                    <Icon size={16} color={status.color} src={status.icon} />
                    {displayText}
                </Typography>
            </StyledWrapper>

            <StyledWrapper {...styles.columnHeadingsWrapper}>
                <Typography {...styles.columnTitle}>{translate("Total")}</Typography>
                <Typography {...styles.columnTitleCentered}>{translate("QTY In Cart")}</Typography>
                <Typography {...styles.columnTitleCentered}>{translate("Schedule QTY")}</Typography>
                <Typography {...styles.columnTitle}>{translate("Schedule Date")}</Typography>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default ScheduleOrderGroupHeader;
